import { TrivialError } from 'app/core/error/error';
import { Globals } from 'app/core/globals';
import { ConditionConfig } from 'collage-designer/collage-designer-config';

export interface ConditionContext
{
  getProductId(): string;
  getSVGStructureByPartId(partId: string): Promise<string | undefined>;
}

export class InvalidConditionError extends TrivialError
{
  protected _name: string = 'InvalidConditionError';
}

export class ConditionMatcher
{
  private catalogStoreId: string;
  private context: ConditionContext;


  private partOrientationMapping: {[partId: string]: string} = {};

  public constructor(catalogStoreId: string,
                     context: ConditionContext)
  {
    this.catalogStoreId = catalogStoreId;
    this.context = context;
  }

  public async areConditionsFullfilled(conditions: Array<ConditionConfig>
                                      ): Promise<boolean>
  {
    for (const c of conditions) {
      if (!await this.isConditionFullfilled(c)) {
        return false;
      }
    }
    return true;
  }

  private async isConditionFullfilled(condition: ConditionConfig
                                      ): Promise<boolean>
  {
    let pattern = undefined;
    const partId = condition.part;

    switch (condition.type) {
      case 'part_is_landscape':
        return (await this.getPartOrientation(partId)) === 'landscape';
      case 'part_is_portrait':
        return (await this.getPartOrientation(partId)) === 'portrait';
      case 'store_matches':
        pattern = new RegExp(condition.value.replace('*', '.*'));
        return pattern.test(this.catalogStoreId);
      case 'store_does_not_match':
        pattern = new RegExp(condition.value.replace('*', '.*'));
        return !pattern.test(this.catalogStoreId);
      case 'mode_is':
        return condition.value === Globals.mode;
      case 'is_available':
        return condition.value === 'yes';
      case 'product_id_contains':
        return this.context.getProductId().includes(condition.value);
      default:
        throw new InvalidConditionError(`condition ${condition.type} unknown`);
    }
  }

  private async getPartOrientation(partId: string): Promise<String>
  {
    if (!this.partOrientationMapping[partId]) {
      const structure = await this.context.getSVGStructureByPartId(
          partId);
      if (!structure) {
        this.partOrientationMapping[partId] = undefined;
      }

      const doc = (new DOMParser()).parseFromString(structure, 'image/svg+xml');
      const width =
          parseFloat((<HTMLElement> doc.firstChild).getAttribute('width'));
      const height =
          parseFloat((<HTMLElement> doc.firstChild).getAttribute('height'));

      this.partOrientationMapping[partId] =  width > height ?
          'landscape' : 'portrait';
    }
    return this.partOrientationMapping[partId];
  }
}
